/**
 * CustomerReviewsSection - Section displaying individual customer reviews
 * Shows a grid of customer review cards with action buttons at the bottom
 */

import type { FC } from "react";
import classNames from "classnames";
import { CustomerReviewCard } from "./CustomerReviewCard";
import { ReviewActionButtons } from "./ReviewActionButtons";
import type { ETrustedReview } from "../ReviewsSection";
import { formatReviewDate } from "../utils";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { useLocale } from "@commercekit/remix";

interface CustomerReviewsSectionProps {
  reviews: ETrustedReview[];
  maxReviews?: number;
  className?: string;
}

export const CustomerReviewsSection: FC<CustomerReviewsSectionProps> = ({
  reviews,
  maxReviews = 6,
  className,
}) => {
  const { $pt } = usePrismicLabels();
  const { localeFullName  } = useLocale();
  // Filter reviews to only show those with rating >= 4
  const filteredReviews = reviews.filter((review) => review.rating >= 4);
  
  // Limit the number of reviews displayed
  const displayedReviews = filteredReviews.slice(0, maxReviews);

  if (filteredReviews.length === 0) {
    return null;
  }

  return (
    <section className={classNames("py-5", className)}>
      <div className="container">
        {/* Section Title */}
        <h2 id="customer-reviews-title" className="sans-serif strong mb-5 reviews_customer_reviews_title">
          {$pt("reviews_customer_reviews_title")}
        </h2>

        {/* Grid Layout - 2 columns on desktop, 1 on mobile */}
        <div className="row mb-5">
          {displayedReviews.map((review) => (
            <div key={review.id} className="col-12 col-md-6 mb-4">
              <CustomerReviewCard
                rating={review.rating}
                comment={review.comment}
                customerName={review.customer.fullName}
                date={formatReviewDate(
                  review.submittedAt || review.createdAt,
                  localeFullName
                )}
                verified={review.verificationType !== "none"}
              />
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <ReviewActionButtons locale={localeFullName}/>
      </div>
    </section>
  );
};

export default CustomerReviewsSection;
